import {I18nKeys} from '@multipass-i18n';
import {BookOpen, LucideIcon, Megaphone, Mic, Pencil, Volume2} from 'lucide-react';
import * as React from 'react';

import {I18nKey, useT} from '../../logic';
import {PhoneFrame} from './PhoneFrame';

type CardStateKey = 'prompt' | 'tones' | 'answer';

interface CardShapeState {
    key: CardStateKey;
    labelKey: I18nKey;
    altKey: I18nKey;
}

interface CardShape {
    key: string;
    nameKey: I18nKey;
    icon: LucideIcon;
    base: string;
    noteKey: I18nKey;
    states: CardShapeState[];
}

const cardShapes: CardShape[] = [
    {
        key: 'write', nameKey: I18nKeys.howItWorks.shapeWrite, icon: Pencil, base: 'review-write', noteKey: I18nKeys.howItWorks.shapeWriteNote,
        states: [
            {key: 'prompt', labelKey: I18nKeys.howItWorks.statePrompt, altKey: I18nKeys.howItWorks.shapeWritePromptAlt},
            {key: 'answer', labelKey: I18nKeys.howItWorks.stateReveal, altKey: I18nKeys.howItWorks.shapeWriteAnswerAlt},
        ],
    },
    {
        key: 'define', nameKey: I18nKeys.howItWorks.shapeDefine, icon: BookOpen, base: 'review-define', noteKey: I18nKeys.howItWorks.shapeDefineNote,
        states: [
            {key: 'prompt', labelKey: I18nKeys.howItWorks.statePrompt, altKey: I18nKeys.howItWorks.shapeDefinePromptAlt},
            {key: 'answer', labelKey: I18nKeys.howItWorks.stateReveal, altKey: I18nKeys.howItWorks.shapeDefineAnswerAlt},
        ],
    },
    {
        key: 'listen', nameKey: I18nKeys.howItWorks.shapeListen, icon: Volume2, base: 'review-listen', noteKey: I18nKeys.howItWorks.shapeListenNote,
        states: [
            {key: 'prompt', labelKey: I18nKeys.howItWorks.statePrompt, altKey: I18nKeys.howItWorks.shapeListenPromptAlt},
            {key: 'answer', labelKey: I18nKeys.howItWorks.stateReveal, altKey: I18nKeys.howItWorks.shapeListenAnswerAlt},
        ],
    },
    {
        key: 'say', nameKey: I18nKeys.howItWorks.shapeSay, icon: Mic, base: 'review-say', noteKey: I18nKeys.howItWorks.shapeSayNote,
        states: [
            {key: 'prompt', labelKey: I18nKeys.howItWorks.statePrompt, altKey: I18nKeys.howItWorks.shapeSayPromptAlt},
            {key: 'tones', labelKey: I18nKeys.howItWorks.stateTones, altKey: I18nKeys.howItWorks.shapeSayTonesAlt},
            {key: 'answer', labelKey: I18nKeys.howItWorks.stateReveal, altKey: I18nKeys.howItWorks.shapeSayAnswerAlt},
        ],
    },
    {
        key: 'readAloud', nameKey: I18nKeys.howItWorks.shapeReadAloud, icon: Megaphone, base: 'review-read-aloud', noteKey: I18nKeys.howItWorks.shapeReadAloudNote,
        states: [
            {key: 'prompt', labelKey: I18nKeys.howItWorks.statePrompt, altKey: I18nKeys.howItWorks.shapeReadAloudPromptAlt},
            {key: 'answer', labelKey: I18nKeys.howItWorks.stateReveal, altKey: I18nKeys.howItWorks.shapeReadAloudAnswerAlt},
        ],
    },
];

const screenshotUrl = (base: string, state: CardStateKey, theme: 'day' | 'night'): string => `/app-screens/${base}-${state}.${theme}.png`;

const pickButtonClass = 'inline-flex min-h-[38px] cursor-pointer items-center gap-[7px] rounded-pill border border-border-subtle bg-surface px-[13px] font-ui text-label font-semibold text-text-secondary hover:border-border hover:text-text-primary aria-pressed:border-action-primary aria-pressed:bg-brand/10 aria-pressed:text-brand-emphasis';

export const CardShapeSwitcher: React.FunctionComponent = () => {
    const t = useT();
    const [shape, setShape] = React.useState<CardShape>(cardShapes[0]);
    const [stateKey, setStateKey] = React.useState<CardStateKey>('prompt');
    const activeState = shape.states.find(state => state.key === stateKey) ?? shape.states[0];

    const selectShape = (nextShape: CardShape): void => {
        setShape(nextShape);
        if (!nextShape.states.some(state => state.key === stateKey)) {
            setStateKey(nextShape.states[0].key);
        }
    };

    return (
        <div className="relative">
            <PhoneFrame
                daySrc={screenshotUrl(shape.base, activeState.key, 'day')}
                nightSrc={screenshotUrl(shape.base, activeState.key, 'night')}
                alt={t(activeState.altKey)}
            />
            {/* Mounted-but-hidden pairs preload the selected shape's other states in both themes. */}
            <div className="hidden" aria-hidden>
                {shape.states.filter(state => state.key !== activeState.key).map(state => (
                    <React.Fragment key={state.key}>
                        <img src={screenshotUrl(shape.base, state.key, 'day')} alt=""/>
                        <img src={screenshotUrl(shape.base, state.key, 'night')} alt=""/>
                    </React.Fragment>
                ))}
            </div>
            <div className="absolute left-1/2 top-full mt-5 flex w-[333px] -translate-x-1/2 flex-col items-center gap-3 max-[900px]:w-full">
                <div role="group" aria-label={t(I18nKeys.howItWorks.pickShapeLabel)} className="flex flex-wrap justify-center gap-1.5">
                    {cardShapes.map(candidate => (
                        <button
                            key={candidate.key}
                            type="button"
                            aria-pressed={candidate.key === shape.key}
                            className={pickButtonClass}
                            onClick={() => selectShape(candidate)}
                        >
                            <candidate.icon size={15} className={candidate.key === shape.key ? 'text-brand-emphasis' : 'text-text-muted'}/>
                            {t(candidate.nameKey)}
                        </button>
                    ))}
                </div>
                <div role="group" aria-label={t(I18nKeys.howItWorks.pickStateLabel)} className="flex flex-wrap justify-center gap-1.5">
                    {shape.states.map(state => (
                        <button
                            key={state.key}
                            type="button"
                            aria-pressed={state.key === activeState.key}
                            className={pickButtonClass}
                            onClick={() => setStateKey(state.key)}
                        >
                            {t(state.labelKey)}
                        </button>
                    ))}
                </div>
                <p className="max-w-[44ch] text-center text-caption text-text-muted">{t(shape.noteKey)}</p>
            </div>
        </div>
    );
};
